/**
 * Settling up, as arithmetic and as sentences: what "settle up" actually
 * writes, and what the plan says about itself.
 *
 * **A settlement is an expense with a very short split.** Settling that Cleo
 * gave Ben 140.00 is one `transactions` row and two lines — Cleo `paid` 14000,
 * Ben `owes` 14000 — which is the same table, the same
 * `SUM(paid) = SUM(owes) = amount_cents` invariant and the same
 * `src/lib/money.ts` as a four-way dinner. `settlementDraft` below builds
 * exactly that, as an `ExpenseDraft` for the write path that already exists;
 * there is no settlement write path, and that is the point. A second way to
 * move a balance is a second place for the ledger to be wrong.
 *
 * **And the words are here because the words are the feature.** A simplified
 * plan can tell somebody to pay a person they have never transacted with, and
 * that — not the arithmetic — is what makes people distrust a settle-up
 * screen. "Three payments instead of six" is the sentence that turns the
 * rerouting from something the app did quietly into something it is showing
 * you. It is a pure function of the summary so a test can pin every branch of
 * it without a DOM, the same way the rest of `src/lib` is.
 *
 * **And the nudge has two answers.** `describeNudge` asks the question;
 * `describeRejection` is the warning the second one carries, because saying
 * "it never arrived" removes a payment somebody else recorded and puts the
 * debt back on them.
 *
 * **The plan suggests; the button asks.** What it suggests is not always what
 * happened: it says "you pay Ben 140.00", and the person pressing the button
 * paid him 100.00 last Tuesday. So Settle up asks — `SettleUpSheet` is what
 * it asks for, and `settleUpProblem` and `describeSettleUp` are what the
 * sheet says back — rather than writing the suggestion as fact and leaving
 * somebody to correct a settlement they never made. They are here rather than
 * in the dialog for the reason everything else in this file is: they are the
 * words, and the words are testable without a DOM.
 */

import type { ExpenseDraft, PlanSummary } from '../api'
import type { UserId } from './money'

/** What a settled payment is called in the expense list. */
export function settlementDescription(toDisplayName: string | null): string {
  return toDisplayName ? `Payment to ${toDisplayName}` : 'Payment'
}

/**
 * One suggested payment as a draft the transactions route will accept.
 *
 * `exact` rather than `equal` with one participant, even though they resolve
 * to the same single line: `exact` says the amount was decided rather than
 * derived, which is what re-opens this in the sheet as the number somebody
 * actually paid. There is no rounding to get wrong here — one participant
 * takes the whole amount — but the mode is also what the sheet reads back, and
 * "equally, between one person" is a strange thing to be shown.
 *
 * `category` is `null` because a repayment is genuinely uncategorised: it did
 * not buy anything, and putting it in a bucket would make it appear in a
 * spending total it is not part of.
 *
 * `projectId` tags the payment to the project it settles — what it was *for*.
 * It is a label like any other project on a transaction: the payment still
 * moves the group's balances, because a project is a view of the group's
 * ledger and not a ledger of its own. Left out or `null`, nothing is sent and
 * the payment is in no project.
 */
export function settlementDraft(payment: {
  fromUserId: UserId
  toUserId: UserId
  toDisplayName?: string | null
  amountCents: number
  occurredOn: string
  projectId?: string | null
}): ExpenseDraft {
  return {
    ...(payment.projectId ? { projectId: payment.projectId } : {}),
    kind: 'repayment',
    description: settlementDescription(payment.toDisplayName ?? null),
    category: null,
    amountCents: payment.amountCents,
    occurredOn: payment.occurredOn,
    payers: [{ userId: payment.fromUserId, amountCents: payment.amountCents }],
    split: {
      mode: 'exact',
      participants: [{ userId: payment.toUserId, amountCents: payment.amountCents }],
    },
  }
}

/**
 * What Settle up asks for: how much actually changed hands, and when.
 *
 * Only the two things the plan cannot know. Who paid whom comes from the
 * payment the button was pressed on, and a settlement has no description, no
 * category and no split worth choosing — `settlementDraft` above decides all
 * three, the same way whichever amount and day this sheet ends up holding.
 */
export type SettleUpSheet = {
  amountCents: number
  /** `YYYY-MM-DD`, the day the money moved — not the day it was entered. */
  occurredOn: string
}

/** The sheet the dialog opens on: the whole suggestion, paid today. */
export function openSettleUp(payment: { amountCents: number }, today: string): SettleUpSheet {
  return { amountCents: payment.amountCents, occurredOn: today }
}

/**
 * What stops this being written, in the words the dialog shows — or `null`
 * when nothing does.
 *
 * The ceiling is the suggested payment, and it is the one rule here worth
 * arguing about. Somebody who hands over more than they owe has not settled
 * up; they have lent, or bought something, and writing it as a repayment
 * would push the balance past zero and out the other side, where the plan
 * would immediately suggest the payment back. Under it is ordinary — a part
 * payment leaves the rest owing, which is what the ledger already says.
 */
export function settleUpProblem(
  sheet: SettleUpSheet,
  payment: { amountCents: number },
  /** `payment.amountCents`, already through `formatMoney`. */
  suggested: string,
): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(sheet.occurredOn)) return 'Pick the day it was paid.'
  if (sheet.amountCents <= 0) return 'Say how much was paid.'
  if (sheet.amountCents > payment.amountCents) {
    return `That is more than the ${suggested} this payment is for. Settle what was actually paid.`
  }
  return null
}

/**
 * What pressing the button would settle, said before it is pressed.
 *
 * A part payment is the case worth a sentence: the number in the box is not
 * the number on the row above it, and "40.00 would still be owing" is the
 * thing somebody wants confirmed before they write it down. `money` is the
 * caller's `formatMoney` bound to the group's currency — this file does not
 * know what currency anything is in, only that a remainder is a subtraction
 * of two integer cent amounts.
 */
export function describeSettleUp(
  sheet: SettleUpSheet,
  payment: { amountCents: number },
  money: (cents: number) => string,
): string {
  if (sheet.amountCents >= payment.amountCents) {
    return `Settles the whole ${money(payment.amountCents)}.`
  }
  const left = payment.amountCents - sheet.amountCents
  return (
    `${money(sheet.amountCents)} of ${money(payment.amountCents)} — ` +
    `${money(left)} would still be owing.`
  )
}

/**
 * The shortcut beside "How much was paid?": the one that answers the question
 * with the whole suggestion.
 *
 * It is worded about the box, not about settling, and that is the whole point
 * of it being here. "All of it — 140.00" sitting under the pad, a quiet button
 * a short way above the quiet Cancel, reads as a second way to submit: press
 * that and the whole amount is what gets written, press Settle up and — what,
 * the number in the box, or the one the plan suggested? Nothing on the screen
 * said. So the verb is `Use`, which is a thing done to a field and cannot be
 * mistaken for a thing done to the ledger, and `describeSettleUp` above goes
 * on being the only sentence about what the press would settle.
 *
 * `whole` rather than `all` matches that sentence — "Settles the whole 140.00"
 * — so pressing this and reading the note back is one word repeated, not two
 * words to reconcile.
 */
export function wholeAmountLabel(
  /** `payment.amountCents`, already through `formatMoney`. */
  suggested: string,
): string {
  return `Use the whole ${suggested}`
}

/**
 * What the plan says about its own shape: the payment count before, the
 * payment count after, and which of them is on the screen.
 *
 * Every branch names both numbers, including the ones where they are the same.
 * "Three payments instead of six" and "three payments, and simplifying would
 * not shorten it" are different reassurances, and a screen that only spoke up
 * when the numbers differed would leave somebody wondering whether the switch
 * had done anything at all.
 */
export function describePlanShape(summary: PlanSummary): string {
  const { simplified, paymentCount, directCount, simplifiedCount } = summary
  const payments = (count: number) => `${count} ${count === 1 ? 'payment' : 'payments'}`

  // The plan on the screen, not the ledger behind it. A group that has settled
  // up through the simplified plan is square while its pairwise record still
  // holds debts that cancel each other out around a cycle — carrying those out
  // would move nobody by a cent — so "nothing to pay" is about what is being
  // offered, and the other branch says what the ledger still holds.
  if (paymentCount === 0) return 'Everybody is square. Nothing to pay.'

  if (simplified) {
    return simplifiedCount < directCount
      ? `${payments(simplifiedCount)} instead of ${directCount}. Who pays whom has been ` +
          'rerouted around the debts that cancel out; nobody ends up a cent better or worse off.'
      : `${payments(simplifiedCount)} — the same as paying everybody you owe directly, so ` +
          'nothing has been rerouted.'
  }

  return simplifiedCount < directCount
    ? `${payments(directCount)}, each one to somebody you actually owe. Simplifying would make ` +
        `it ${simplifiedCount}, without moving anybody's balance.`
    : `${payments(directCount)}, each one to somebody you actually owe. Simplifying would not ` +
        'make it shorter.'
}

/**
 * The nudge, as the recipient reads it. It asks rather than asserts, because
 * this app has no way of knowing whether the money arrived — that is the whole
 * reason it is asking.
 */
export function describeNudge(
  fromDisplayName: string | null,
  /** Already money, from `formatMoney` — this module does no arithmetic. */
  amount: string,
  /** Already a day, from `formatDay`, for the same reason. */
  day: string,
  /** The project the payment was tagged to, if it was — what it was for. */
  projectName: string | null = null,
): string {
  const who = fromDisplayName ?? 'Somebody'
  const what = projectName ? ` for ${projectName}` : ''
  return `${who} says they paid you ${amount}${what} on ${day}. Did it land?`
}

/**
 * What saying "no" to that question does, said before it is done.
 *
 * The nudge's two answers are not symmetrical and the words have to admit it.
 * "Yes, it landed" changes nothing anybody can see — the balance moved when
 * the payment was recorded — but "no" takes the payment off the ledger and
 * puts the debt back on the person who says they paid it. That is somebody
 * else's money being un-recorded on your word, so it is worth a sentence and
 * a second press rather than being one tap away from the button beside it.
 */
export function describeRejection(
  fromDisplayName: string | null,
  /** Already money, from `formatMoney` — this module does no arithmetic. */
  amount: string,
): string {
  const who = fromDisplayName ?? 'Somebody'
  return (
    `This removes the payment and puts the ${amount} back as owing. ` +
    `${who} will be told it did not arrive.`
  )
}
